import { AlertTriangle, RotateCcw, ScrollText, ShieldAlert } from 'lucide-react';
import { useId, useState } from 'react';

import { Bouton } from '@/shared/ui/Bouton';
import { Etiquette, Saisie, ZoneTexte } from '@/shared/ui/Champ';
import {
  ContenuDialogue,
  CorpsDialogue,
  DescriptionDialogue,
  Dialogue,
  EnTeteDialogue,
  PiedDialogue,
  TitreDialogue,
} from '@/shared/ui/Dialogue';

import type { ActeSensible } from './actesSensibles';

const MOT_DE_CONFIRMATION = 'CONFIRMER';

/**
 * Boite de confirmation d'un acte sensible.
 *
 * LE SEUL CHEMIN vers un acte du catalogue. Aucun ecran ne declenche une suspension, un
 * effacement ou une consultation de donnees personnelles autrement que par ce composant :
 * c'est ce qui rend uniforme la double confirmation exigee au §3, au lieu de dependre du
 * soin apporte a chaque bouton.
 *
 * Elle affiche quatre choses, toujours dans le meme ordre, parce que ce sont les quatre
 * questions qu'on se pose avant de cliquer :
 *   1. ce que l'acte fait concretement ;
 *   2. s'il est reversible, et comment ;
 *   3. ce qui sera consigne au journal — annonce AVANT, pas decouvert apres ;
 *   4. ce qu'on attend de l'operateur (motif, mot de confirmation).
 *
 * Le bouton de validation reste inactif tant que les exigences ne sont pas satisfaites.
 * Ce n'est pas une politesse : le serveur refuserait l'acte, et un aller-retour perdu sur
 * une action de ce poids est une occasion de se tromper de cible entre-temps.
 */
export function DialogueActeSensible({
  acte,
  cible,
  enCours,
  onConfirmer,
  onFermer,
}: {
  acte: ActeSensible;
  /** Ce sur quoi porte l'acte, en clair. Affiche tel quel a l'operateur. */
  cible: string;
  enCours?: boolean;
  onConfirmer: (motif: string) => void;
  onFermer: () => void;
}) {
  const identifiant = useId();
  const [motif, setMotif] = useState('');
  const [confirmation, setConfirmation] = useState('');

  const motifFourni = !acte.motifRequis || motif.trim().length >= 10;
  const motConfirme =
    !acte.confirmationRenforcee || confirmation.trim().toUpperCase() === MOT_DE_CONFIRMATION;

  const pretAValider = motifFourni && motConfirme && !enCours;

  return (
    <Dialogue open onOpenChange={(ouvert) => !ouvert && onFermer()}>
      <ContenuDialogue>
        <EnTeteDialogue>
          <TitreDialogue>{acte.libelle}</TitreDialogue>
          <DescriptionDialogue>{cible}</DescriptionDialogue>
        </EnTeteDialogue>

        <CorpsDialogue className="space-y-4">
          <p className="text-texte-doux">{acte.consequence}</p>

          {/* 2. Reversibilite — annoncee meme quand elle est bonne nouvelle. */}
          <p className="flex items-start gap-2 rounded-[--radius-astro] border border-bordure p-2.5">
            {acte.reversibilite ? (
              <>
                <RotateCcw className="mt-0.5 size-3.5 shrink-0 text-texte-faible" aria-hidden />
                <span className="text-texte-doux">{acte.reversibilite}</span>
              </>
            ) : (
              <>
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-danger" aria-hidden />
                <span className="font-medium text-danger">
                  Cet acte est irreversible. Il n’existe aucun moyen de revenir en arriere.
                </span>
              </>
            )}
          </p>

          {/* 3. Ce qui sera consigne. Annonce, pas subi. */}
          <p className="flex items-start gap-2 text-texte-faible">
            <ScrollText className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            <span>
              Consigne au journal d’audit : votre compte, l’horodatage, l’acte{' '}
              <code className="text-texte-doux">{acte.action}</code>
              {acte.motifRequis ? ', et le motif ci-dessous' : ''}.
            </span>
          </p>

          {acte.donneesPersonnelles ? (
            <p className="bandeau-sensible flex items-start gap-2 rounded-r-[--radius-astro] p-2.5">
              <ShieldAlert className="mt-0.5 size-3.5 shrink-0 text-sensible" aria-hidden />
              <span className="text-texte">
                Cet acte porte sur des donnees personnelles. Il sera marque comme tel dans le
                journal et restera filtrable a ce titre.
              </span>
            </p>
          ) : null}

          {/* 4. Ce qu'on attend de l'operateur. */}
          {acte.motifRequis ? (
            <div className="space-y-1.5">
              <Etiquette htmlFor={`${identifiant}-motif`}>
                Motif <span className="text-texte-faible">(10 caracteres minimum)</span>
              </Etiquette>

              <ZoneTexte
                id={`${identifiant}-motif`}
                rows={3}
                value={motif}
                onChange={(evenement) => setMotif(evenement.target.value)}
                placeholder="Demande du client, ticket 4821, decision judiciaire…"
                autoFocus
              />

              <p className="text-texte-faible">
                Ecrivez ce qui vous permettra, dans six mois, de justifier cet acte.
              </p>
            </div>
          ) : null}

          {acte.confirmationRenforcee ? (
            <div className="space-y-1.5">
              <Etiquette htmlFor={`${identifiant}-confirmation`}>
                Tapez « {MOT_DE_CONFIRMATION} » pour valider
              </Etiquette>

              <Saisie
                id={`${identifiant}-confirmation`}
                value={confirmation}
                onChange={(evenement) => setConfirmation(evenement.target.value)}
                autoComplete="off"
              />
            </div>
          ) : null}
        </CorpsDialogue>

        <PiedDialogue>
          <Bouton variante="secondaire" onClick={onFermer}>
            Annuler
          </Bouton>

          <Bouton
            variante={acte.reversibilite === null ? 'danger' : 'principal'}
            disabled={!pretAValider}
            onClick={() => onConfirmer(motif.trim())}
          >
            {acte.libelle}
          </Bouton>
        </PiedDialogue>
      </ContenuDialogue>
    </Dialogue>
  );
}
